import { Check, Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { Section, SectionProgress } from "./types";
import { cn } from "@/lib/utils";

export const REVIEW_ID = "__review__";

function Dot({ section, state }: { section: Section; state: SectionProgress }) {
  if (section.audience === "info") {
    return (
      <span aria-hidden="true" className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-surface-sunken text-fg-muted">
        <Info className="h-3.5 w-3.5" />
      </span>
    );
  }
  return (
    <span
      aria-hidden="true"
      data-progress={state}
      className={cn(
        "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2",
        state === "not_started" && "border-border-strong bg-surface",
        state === "in_progress" && "border-accent bg-surface",
        state === "done" && "border-secondary bg-secondary text-fg-inverse",
      )}
    >
      {state === "done" ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
    </span>
  );
}

export function SectionNav({
  sections,
  progress,
  activeId,
  onSelect,
  includeReview = false,
  label,
}: {
  sections: Section[];
  progress: Record<string, SectionProgress>;
  activeId: string;
  onSelect: (id: string) => void;
  includeReview?: boolean;
  label?: string;
}) {
  const { t } = useTranslation();
  const stateOf = (s: Section): SectionProgress => progress[s.id] ?? "not_started";
  const statusText = (s: Section) => (s.audience === "info" ? t("questionnaire.progress.info") : t(`questionnaire.progress.${stateOf(s)}`));

  return (
    <>
      <nav aria-label={label ?? t("questionnaire.sections")}>
        <ol className="flex flex-col gap-1">
          {sections.map((s) => {
            const active = s.id === activeId;
            return (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => onSelect(s.id)}
                  aria-current={active ? "step" : undefined}
                  className={cn(
                    "flex min-h-11 w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors",
                    active ? "bg-secondary-subtle font-semibold text-heading" : "text-fg hover:bg-surface-muted",
                  )}
                >
                  <Dot section={s} state={stateOf(s)} />
                  <span className="flex-1">{s.title}</span>
                  <span className="sr-only">, {statusText(s)}</span>
                </button>
              </li>
            );
          })}
          {includeReview ? (
            <li className="mt-2 border-t border-border pt-2">
              <button
                type="button"
                onClick={() => onSelect(REVIEW_ID)}
                aria-current={activeId === REVIEW_ID ? "step" : undefined}
                className={cn(
                  "flex min-h-11 w-full items-center gap-3 rounded-md px-3 py-2 text-left font-heading font-semibold transition-colors",
                  activeId === REVIEW_ID ? "bg-secondary-subtle text-heading" : "text-heading hover:bg-surface-muted",
                )}
              >
                {t("questionnaire.reviewNav")}
              </button>
            </li>
          ) : null}
        </ol>
      </nav>
    </>
  );
}
